// Add your custom JS here.

import Swiper from 'swiper';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';

/**
 * Site header.
 *
 * Swaps the transparent hero header for the white one once the page is
 * scrolled past the bar, and drives the mobile menu panel.
 */
( function() {
	'use strict';

	var header = document.querySelector( '.site-header' );

	if ( ! header ) {
		return;
	}

	// The header turns solid once the top bar above it has scrolled out of view.
	var topbar = document.querySelector( '.site-topbar' );
	var ticking = false;

	var syncScrollState = function() {
		var threshold = topbar ? topbar.offsetHeight : 0;

		header.classList.toggle( 'is-scrolled', window.scrollY > threshold );
		ticking = false;
	};

	window.addEventListener( 'scroll', function() {
		if ( ! ticking ) {
			window.requestAnimationFrame( syncScrollState );
			ticking = true;
		}
	}, { passive: true } );

	syncScrollState();

	// Mobile panel.
	var toggler = header.querySelector( '.site-header__toggler' );

	if ( toggler ) {
		toggler.addEventListener( 'click', function() {
			var isOpen = header.classList.toggle( 'is-open' );
			toggler.setAttribute( 'aria-expanded', isOpen ? 'true' : 'false' );
		} );

		// Close the panel when the viewport grows back to the desktop layout.
		window.addEventListener( 'resize', function() {
			if ( window.innerWidth >= 992 && header.classList.contains( 'is-open' ) ) {
				header.classList.remove( 'is-open' );
				toggler.setAttribute( 'aria-expanded', 'false' );
			}
		} );
	}
}() );


/**
 * Hero slider.
 *
 * Cross-fades between slides. Autoplay is a block setting, and is dropped for
 * visitors who ask for reduced motion.
 */
( function() {
	'use strict';

	var heroes = document.querySelectorAll( '.hero' );

	if ( ! heroes.length ) {
		return;
	}

	var prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	Array.prototype.forEach.call( heroes, function( hero ) {
		var container = hero.querySelector( '.hero__swiper' );

		if ( ! container ) {
			return;
		}

		var slideCount = container.querySelectorAll( '.swiper-slide' ).length;

		if ( slideCount < 2 ) {
			return;
		}

		var autoplayWanted = 'true' === hero.getAttribute( 'data-autoplay' ) && ! prefersReducedMotion;
		var delay = parseInt( hero.getAttribute( 'data-delay' ), 10 ) || 6000;

		// The fade effect only drops the opacity of the slides that are off, so
		// their heading and button stay in the tab order. Mark them inert.
		var syncInert = function( swiper ) {
			Array.prototype.forEach.call( swiper.slides, function( slide ) {
				slide.inert = ! slide.classList.contains( 'swiper-slide-active' );
			} );
		};

		new Swiper( container, {
			modules: [ Autoplay, EffectFade, Navigation, Pagination ],
			effect: 'fade',
			fadeEffect: { crossFade: true },
			speed: 800,
			loop: true,
			autoHeight: false,
			autoplay: autoplayWanted ? {
				delay: delay,
				disableOnInteraction: false,
				pauseOnMouseEnter: true,
			} : false,
			navigation: {
				prevEl: hero.querySelector( '.hero__prev' ),
				nextEl: hero.querySelector( '.hero__next' ),
			},
			pagination: {
				el: hero.querySelector( '.hero__pagination' ),
				clickable: true,
			},
			a11y: {
				enabled: true,
			},
			on: {
				init: syncInert,
				slideChange: syncInert,
			},
		} );
	} );
}() );


/**
 * History timeline.
 *
 * A carousel of milestone cards tied to the year axis underneath: the axis
 * follows the carousel, and clicking a year moves the carousel to it.
 */
( function() {
	'use strict';

	var sections = document.querySelectorAll( '.history' );

	if ( ! sections.length ) {
		return;
	}

	Array.prototype.forEach.call( sections, function( section ) {
		var container = section.querySelector( '.history__swiper' );

		if ( ! container ) {
			return;
		}

		var markers = section.querySelectorAll( '.history__marker' );

		var syncMarkers = function( swiper ) {
			Array.prototype.forEach.call( markers, function( marker, index ) {
				marker.classList.toggle( 'is-active', index === swiper.activeIndex );
			} );
		};

		var swiper = new Swiper( container, {
			modules: [ Navigation, Pagination ],
			slidesPerView: 'auto',
			spaceBetween: 40,
			speed: 500,
			watchOverflow: true,
			// Slide widths come from the stylesheet, so re-measure whenever the
			// layout around the carousel settles.
			observer: true,
			observeParents: true,
			navigation: {
				prevEl: section.querySelector( '.history__prev' ),
				nextEl: section.querySelector( '.history__next' ),
			},
			pagination: {
				el: section.querySelector( '.history__pagination' ),
				clickable: true,
			},
			breakpoints: {
				0: { spaceBetween: 16 },
				768: { spaceBetween: 40 },
			},
			on: {
				init: syncMarkers,
				slideChange: syncMarkers,
			},
		} );

		// Images and webfonts land after the footer script runs; without this the
		// first measurement can be the only one Swiper ever takes.
		window.addEventListener( 'load', function() {
			swiper.update();
		} );

		Array.prototype.forEach.call( markers, function( marker ) {
			var button = marker.querySelector( '.history__marker-btn' );

			if ( ! button ) {
				return;
			}

			button.addEventListener( 'click', function() {
				swiper.slideTo( parseInt( button.getAttribute( 'data-slide' ), 10 ) || 0 );
			} );
		} );
	} );
}() );
/**
 * Before / after gallery.
 *
 * Each tab owns a carousel of its own. Hidden panels cannot be measured, so a
 * panel is updated the moment it is shown rather than at page load.
 */
( function() {
	'use strict';

	var sections = document.querySelectorAll( '.gallery' );

	if ( ! sections.length ) {
		return;
	}

	Array.prototype.forEach.call( sections, function( section ) {
		var panels = section.querySelectorAll( '.gallery__panel' );
		var tabs = section.querySelectorAll( '.gallery__tab' );
		var swipers = [];

		Array.prototype.forEach.call( panels, function( panel ) {
			var container = panel.querySelector( '.gallery__swiper' );

			if ( ! container ) {
				swipers.push( null );
				return;
			}

			swipers.push( new Swiper( container, {
				modules: [ Navigation, Pagination ],
				slidesPerView: 'auto',
				spaceBetween: 32,
				speed: 500,
				observer: true,
				observeParents: true,
				navigation: {
					prevEl: panel.querySelector( '.gallery__prev' ),
					nextEl: panel.querySelector( '.gallery__next' ),
				},
				pagination: {
					el: panel.querySelector( '.gallery__pagination' ),
					clickable: true,
				},
				breakpoints: {
					0: { spaceBetween: 16 },
					768: { spaceBetween: 32 },
				},
			} ) );
		} );

		window.addEventListener( 'load', function() {
			swipers.forEach( function( swiper ) {
				if ( swiper ) {
					swiper.update();
				}
			} );
		} );

		Array.prototype.forEach.call( tabs, function( tab, index ) {
			tab.addEventListener( 'click', function() {
				Array.prototype.forEach.call( tabs, function( other, otherIndex ) {
					var isCurrent = otherIndex === index;

					other.classList.toggle( 'is-active', isCurrent );
					other.setAttribute( 'aria-selected', isCurrent ? 'true' : 'false' );
				} );

				Array.prototype.forEach.call( panels, function( panel, panelIndex ) {
					var isCurrent = panelIndex === index;

					panel.classList.toggle( 'is-active', isCurrent );
					panel.hidden = ! isCurrent;
				} );

				// The panel had no size while it was hidden, so Swiper has to
				// take its measurements again now that it is on screen.
				if ( swipers[ index ] ) {
					swipers[ index ].update();
				}
			} );
		} );
	} );
}() );
/**
 * Testimonials carousel.
 *
 * Cards are centred with the neighbours bleeding off both edges. Looping needs
 * enough cards to fill the track twice over, so it is only switched on once
 * there are enough of them.
 */
( function() {
	'use strict';

	var sections = document.querySelectorAll( '.reviews' );

	if ( ! sections.length ) {
		return;
	}

	Array.prototype.forEach.call( sections, function( section ) {
		var container = section.querySelector( '.reviews__swiper' );

		if ( ! container ) {
			return;
		}

		var wrapper = container.querySelector( '.swiper-wrapper' );
		var originals = Array.prototype.slice.call( container.querySelectorAll( '.swiper-slide' ) );

		if ( ! wrapper || originals.length < 2 ) {
			return;
		}

		// Swiper only loops when the slides cover the visible track twice over,
		// and quietly falls back to a plain slider when they do not - which
		// leaves a gap at the start. Repeating the set until there are enough
		// keeps the row full at every position. The copies are hidden from
		// assistive tech so the reviews are only announced once.
		var cardWidth = originals[ 0 ].getBoundingClientRect().width + 24;
		var perView = cardWidth > 0 ? Math.ceil( container.clientWidth / cardWidth ) : 4;
		var needed = Math.max( ( perView + 1 ) * 2, 8 );

		while ( wrapper.children.length < needed ) {
			originals.forEach( function( slide ) {
				var copy = slide.cloneNode( true );

				copy.setAttribute( 'aria-hidden', 'true' );
				wrapper.appendChild( copy );
			} );
		}

		var swiper = new Swiper( container, {
			modules: [ Navigation ],
			slidesPerView: 'auto',
			spaceBetween: 24,
			centeredSlides: true,
			loop: true,
			speed: 500,
			observer: true,
			observeParents: true,
			navigation: {
				prevEl: section.querySelector( '.reviews__prev' ),
				nextEl: section.querySelector( '.reviews__next' ),
			},
			breakpoints: {
				0: { spaceBetween: 16 },
				768: { spaceBetween: 24 },
			},
		} );

		window.addEventListener( 'load', function() {
			swiper.update();
		} );
	} );
}() );
/**
 * Tab groups.
 *
 * Any container marked .js-tabs with [data-tab-button] and [data-tab-panel]
 * children. Kept generic so blocks that only switch panels need no code of
 * their own.
 */
( function() {
	'use strict';

	var groups = document.querySelectorAll( '.js-tabs' );

	if ( ! groups.length ) {
		return;
	}

	Array.prototype.forEach.call( groups, function( group ) {
		var buttons = group.querySelectorAll( '[data-tab-button]' );
		var panels = group.querySelectorAll( '[data-tab-panel]' );

		Array.prototype.forEach.call( buttons, function( button, index ) {
			button.addEventListener( 'click', function() {
				Array.prototype.forEach.call( buttons, function( other, otherIndex ) {
					var isCurrent = otherIndex === index;

					other.classList.toggle( 'is-active', isCurrent );
					other.setAttribute( 'aria-selected', isCurrent ? 'true' : 'false' );
				} );

				Array.prototype.forEach.call( panels, function( panel, panelIndex ) {
					panel.hidden = panelIndex !== index;
				} );
			} );
		} );
	} );
}() );
/**
 * Accordions.
 *
 * <details> shows and hides its panel outright, so the services and FAQ blocks
 * open with a jump. This animates the panel open and closed instead.
 *
 * The vertical padding is animated along with the height. Animating height
 * alone leaves the padding at full size the whole time, which makes the panel
 * appear to snap open before it starts moving.
 *
 * The exclusive-group behaviour of the name attribute has to be taken over as
 * well: while the browser owns it, opening one panel force-closes its sibling
 * the instant we ask, cutting the closing animation short. So the attribute is
 * moved aside and the group is tracked here.
 */
( function() {
	'use strict';

	var items = document.querySelectorAll( 'details.service, details.faq-item' );

	if ( ! items.length ) {
		return;
	}

	// Anyone who asked for less motion keeps the plain, instant behaviour.
	if ( window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
		return;
	}

	var duration = 300;
	var easing = 'cubic-bezier(.4, 0, .2, 1)';

	var panelOf = function( details ) {
		var summary = details.querySelector( 'summary' );

		return summary ? summary.nextElementSibling : null;
	};

	// Reads the resting size before any inline styles are put on the panel.
	var measure = function( panel ) {
		var styles = window.getComputedStyle( panel );

		return {
			height: panel.scrollHeight,
			top: styles.paddingTop,
			bottom: styles.paddingBottom,
		};
	};

	var clear = function( details, panel ) {
		panel.style.height = '';
		panel.style.paddingTop = '';
		panel.style.paddingBottom = '';
		panel.style.overflow = '';
		delete details.dataset.animating;
	};

	var run = function( details, panel, frames, onEnd ) {
		details.dataset.animating = 'true';
		panel.style.overflow = 'hidden';

		// Pinning the starting values keeps the browser from painting a frame of
		// the finished state before the animation takes over.
		panel.style.height = frames[ 0 ].height;
		panel.style.paddingTop = frames[ 0 ].paddingTop;
		panel.style.paddingBottom = frames[ 0 ].paddingBottom;

		var animation = panel.animate( frames, { duration: duration, easing: easing } );

		animation.onfinish = function() {
			if ( onEnd ) {
				onEnd();
			}

			clear( details, panel );
		};

		animation.oncancel = function() {
			clear( details, panel );
		};
	};

	var collapse = function( details ) {
		var panel = panelOf( details );

		if ( ! panel ) {
			details.open = false;
			return;
		}

		var size = measure( panel );

		run(
			details,
			panel,
			[
				{ height: size.height + 'px', paddingTop: size.top, paddingBottom: size.bottom, opacity: 1 },
				{ height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 },
			],
			function() {
				details.open = false;
			}
		);
	};

	var expand = function( details ) {
		var panel = panelOf( details );

		if ( ! panel ) {
			details.open = true;
			return;
		}

		details.open = true;

		var size = measure( panel );

		run(
			details,
			panel,
			[
				{ height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 },
				{ height: size.height + 'px', paddingTop: size.top, paddingBottom: size.bottom, opacity: 1 },
			]
		);
	};

	Array.prototype.forEach.call( items, function( details ) {
		var group = details.getAttribute( 'name' );

		if ( group ) {
			details.setAttribute( 'data-group', group );
			details.removeAttribute( 'name' );
		}

		var summary = details.querySelector( 'summary' );

		if ( ! summary || ! panelOf( details ) ) {
			return;
		}

		summary.addEventListener( 'click', function( event ) {
			event.preventDefault();

			if ( details.dataset.animating ) {
				return;
			}

			if ( details.open ) {
				collapse( details );
				return;
			}

			var currentGroup = details.getAttribute( 'data-group' );

			if ( currentGroup ) {
				var siblings = document.querySelectorAll( '[data-group="' + currentGroup + '"]' );

				Array.prototype.forEach.call( siblings, function( sibling ) {
					if ( sibling !== details && sibling.open && ! sibling.dataset.animating ) {
						collapse( sibling );
					}
				} );
			}

			expand( details );
		} );
	} );
}() );
/**
 * Form selects.
 *
 * Formidable renders the placeholder as a normal empty option, so a visitor can
 * pick it again and wipe their answer. Disabling it keeps it on screen as the
 * resting state but takes it out of the choices, and the grey class makes the
 * field read as empty until something real is chosen.
 */
( function() {
	'use strict';

	var selects = document.querySelectorAll( '.frm_forms select' );

	if ( ! selects.length ) {
		return;
	}

	Array.prototype.forEach.call( selects, function( select ) {
		var first = select.options[ 0 ];

		if ( ! first || '' !== first.value ) {
			return;
		}

		first.disabled = true;

		var syncState = function() {
			select.classList.toggle( 'is-placeholder', '' === select.value );
		};

		select.addEventListener( 'change', syncState );
		syncState();
	} );
}() );

/**
 * Article list load more.
 *
 * The button carries the state it needs in data attributes: the page it has
 * reached, the batch size and the category the list was cut to. Each click asks
 * admin-ajax for the next batch, appends the cards it gets back and hides
 * itself once the server says there is nothing left.
 */
( function() {
	'use strict';

	var buttons = document.querySelectorAll( '[data-post-list-more]' );

	if ( ! buttons.length || 'undefined' === typeof window.rugAjax ) {
		return;
	}

	Array.prototype.forEach.call( buttons, function( button ) {
		var section = button.closest( '.post-list' );
		var grid = section ? section.querySelector( '[data-post-list-grid]' ) : null;

		if ( ! grid ) {
			return;
		}

		var label = button.getAttribute( 'data-label' ) || button.textContent;
		var loadingLabel = button.getAttribute( 'data-loading-label' ) || label;
		var busy = false;

		button.addEventListener( 'click', function() {
			if ( busy ) {
				return;
			}

			busy = true;
			button.disabled = true;
			button.textContent = loadingLabel;

			var body = new FormData();
			body.append( 'action', 'rug_load_posts' );
			body.append( 'nonce', window.rugAjax.nonce );
			body.append( 'page', parseInt( button.getAttribute( 'data-page' ), 10 ) + 1 );
			body.append( 'per_page', button.getAttribute( 'data-per-page' ) );
			body.append( 'category', button.getAttribute( 'data-category' ) );

			window.fetch( window.rugAjax.url, {
				method: 'POST',
				credentials: 'same-origin',
				body: body
			} ).then( function( response ) {
				return response.json();
			} ).then( function( payload ) {
				if ( ! payload || ! payload.success ) {
					throw new Error( 'Unexpected response' );
				}

				var holder = document.createElement( 'div' );
				holder.innerHTML = payload.data.html;

				var added = holder.querySelectorAll( '.post-card' );

				Array.prototype.forEach.call( added, function( card ) {
					card.classList.add( 'post-card--appended' );
					grid.appendChild( card );
				} );

				button.setAttribute( 'data-page', parseInt( button.getAttribute( 'data-page' ), 10 ) + 1 );

				if ( payload.data.hasMore ) {
					button.disabled = false;
					button.textContent = label;
					busy = false;

					// Let a keyboard user carry on from the first card that arrived.
					if ( added.length && document.activeElement === button ) {
						added[ 0 ].setAttribute( 'tabindex', '-1' );
					}
				} else {
					button.remove();
				}
			} ).catch( function() {
				button.disabled = false;
				button.textContent = label;
				busy = false;
			} );
		} );
	} );
}() );

/**
 * Photo gallery.
 *
 * Two jobs: the button reveals the next batch of photographs, and a click on
 * one of them opens the lightbox. Every photograph is a plain link to its file,
 * so with the script off, or before it runs, the gallery still works — the
 * click handler is what turns those links into the overlay.
 *
 * The overlay is a native <dialog>: Esc, the focus trap and the inert page
 * behind it come from the browser instead of from here.
 */
( function() {
	'use strict';

	var galleries = document.querySelectorAll( '[data-gallery]' );

	if ( ! galleries.length ) {
		return;
	}

	var CLOSE_ICON = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
	var ARROW_ICON = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M20 12H4M4 12l6-6M4 12l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

	var box = null;
	var items = [];
	var index = 0;

	// Reveal the next batch.
	Array.prototype.forEach.call( galleries, function( gallery ) {
		var more = gallery.querySelector( '[data-gallery-more]' );

		if ( ! more ) {
			return;
		}

		more.addEventListener( 'click', function() {
			var hidden = gallery.querySelectorAll( '[data-gallery-item][hidden]' );
			var step = parseInt( more.getAttribute( 'data-step' ), 10 ) || hidden.length;

			Array.prototype.slice.call( hidden, 0, step ).forEach( function( item ) {
				item.hidden = false;
			} );

			if ( ! gallery.querySelectorAll( '[data-gallery-item][hidden]' ).length ) {
				more.remove();
			}
		} );
	} );

	// The lightbox is only built the first time one is actually opened.
	function build() {
		var dialog = document.createElement( 'dialog' );

		dialog.className = 'lightbox';
		dialog.setAttribute( 'aria-label', 'Gallery' );
		dialog.innerHTML =
			'<button type="button" class="lightbox__action lightbox__close" data-close aria-label="Close">' + CLOSE_ICON + '</button>' +
			'<button type="button" class="lightbox__action lightbox__prev" data-prev aria-label="Previous photograph">' + ARROW_ICON + '</button>' +
			'<button type="button" class="lightbox__action lightbox__next" data-next aria-label="Next photograph">' + ARROW_ICON + '</button>' +
			'<figure class="lightbox__stage" data-stage><img class="lightbox__image" data-image alt=""></figure>' +
			'<div class="lightbox__thumbs" data-thumbs></div>';

		document.body.appendChild( dialog );

		box = {
			dialog: dialog,
			image: dialog.querySelector( '[data-image]' ),
			thumbs: dialog.querySelector( '[data-thumbs]' ),
			prev: dialog.querySelector( '[data-prev]' ),
			next: dialog.querySelector( '[data-next]' ),
			stage: dialog.querySelector( '[data-stage]' )
		};

		// The next arrow is the same glyph turned around.
		box.next.firstChild.style.transform = 'rotate(180deg)';

		box.prev.addEventListener( 'click', function() {
			show( index - 1 );
		} );

		box.next.addEventListener( 'click', function() {
			show( index + 1 );
		} );

		dialog.querySelector( '[data-close]' ).addEventListener( 'click', function() {
			dialog.close();
		} );

		// A click on the empty space around the photograph closes it too.
		box.stage.addEventListener( 'click', function( event ) {
			if ( event.target === box.stage ) {
				dialog.close();
			}
		} );

		dialog.addEventListener( 'click', function( event ) {
			if ( event.target === dialog ) {
				dialog.close();
			}
		} );

		dialog.addEventListener( 'keydown', function( event ) {
			if ( 'ArrowLeft' === event.key ) {
				event.preventDefault();
				show( index - 1 );
			} else if ( 'ArrowRight' === event.key ) {
				event.preventDefault();
				show( index + 1 );
			}
		} );

		dialog.addEventListener( 'close', function() {
			document.documentElement.style.overflow = '';
		} );
	}

	function renderThumbs() {
		box.thumbs.innerHTML = '';
		box.thumbs.hidden = items.length < 2;
		box.prev.hidden = items.length < 2;
		box.next.hidden = items.length < 2;

		items.forEach( function( item, position ) {
			var thumb = document.createElement( 'button' );

			thumb.type = 'button';
			thumb.className = 'lightbox__thumb';
			thumb.setAttribute( 'aria-label', 'Photograph ' + ( position + 1 ) );
			thumb.innerHTML = '<img src="' + item.thumb + '" alt="" loading="lazy">';

			thumb.addEventListener( 'click', function() {
				show( position );
			} );

			box.thumbs.appendChild( thumb );
		} );
	}

	function show( position ) {
		if ( ! items.length ) {
			return;
		}

		// Wrap around rather than stopping at either end.
		index = ( position + items.length ) % items.length;

		box.image.src = items[ index ].full;
		box.image.alt = items[ index ].alt;

		Array.prototype.forEach.call( box.thumbs.children, function( thumb, position2 ) {
			if ( position2 === index ) {
				thumb.setAttribute( 'aria-current', 'true' );
				thumb.scrollIntoView( { block: 'nearest', inline: 'nearest' } );
			} else {
				thumb.removeAttribute( 'aria-current' );
			}
		} );

		// Have the neighbours ready before they are asked for.
		[ index + 1, index - 1 ].forEach( function( neighbour ) {
			var near = items[ ( neighbour + items.length ) % items.length ];

			if ( near ) {
				new Image().src = near.full;
			}
		} );
	}

	function open( gallery, item ) {
		var links = Array.prototype.slice.call( gallery.querySelectorAll( '[data-gallery-item]' ) );

		items = links.map( function( link ) {
			var image = link.querySelector( 'img' );

			return {
				full: link.getAttribute( 'href' ),
				thumb: image ? image.currentSrc || image.src : link.getAttribute( 'href' ),
				alt: image ? image.alt : ''
			};
		} );

		if ( ! box ) {
			build();
		}

		renderThumbs();
		show( links.indexOf( item ) );

		document.documentElement.style.overflow = 'hidden';
		box.dialog.showModal();
	}

	Array.prototype.forEach.call( galleries, function( gallery ) {
		gallery.addEventListener( 'click', function( event ) {
			var item = event.target.closest( '[data-gallery-item]' );

			// Let a modified click open the file the way the reader asked for,
			// and leave the link alone if the browser has no modal dialog.
			if ( ! item || event.metaKey || event.ctrlKey || event.shiftKey || 'undefined' === typeof HTMLDialogElement || 'function' !== typeof HTMLDialogElement.prototype.showModal ) {
				return;
			}

			event.preventDefault();
			open( gallery, item );
		} );
	} );
}() );

/**
 * Video slider.
 *
 * A carousel of posters. Each poster is a button holding a <template> with its
 * player in it, so the embed is only built — and only reaches YouTube or Vimeo —
 * when a visitor presses play. Playing one stops any other that is running, and
 * changing slide stops it too, which is what a reader expects when the card
 * scrolls away.
 */
( function() {
	'use strict';

	var sections = document.querySelectorAll( '.videos' );

	if ( ! sections.length ) {
		return;
	}

	Array.prototype.forEach.call( sections, function( section ) {
		var container = section.querySelector( '.videos__swiper' );

		if ( ! container ) {
			return;
		}

		// Put a player back to its poster so nothing keeps playing off screen.
		var reset = function( player ) {
			var poster = player.querySelector( '[data-poster]' );

			if ( ! poster ) {
				return;
			}

			var frame = player.querySelector( '.video-card__frame' );

			if ( frame ) {
				frame.remove();
			}

			poster.hidden = false;
		};

		var players = section.querySelectorAll( '[data-video-player]' );

		Array.prototype.forEach.call( players, function( player ) {
			var button = player.querySelector( '[data-video-open]' );
			var embed = player.querySelector( '[data-video-embed]' );

			if ( ! button || ! embed ) {
				return;
			}

			// Marked here rather than in the template: reset() only ever has to
			// deal with cards that can actually play something.
			button.setAttribute( 'data-poster', '' );

			button.addEventListener( 'click', function() {
				Array.prototype.forEach.call( players, reset );

				player.appendChild( embed.content.cloneNode( true ) );
				button.hidden = true;
			} );
		} );

		var swiper = new Swiper( container, {
			modules: [ Navigation, Pagination ],
			slidesPerView: 'auto',
			spaceBetween: 32,
			speed: 500,
			watchOverflow: true,
			// Slide widths come from the stylesheet, so re-measure whenever the
			// layout around the carousel settles.
			observer: true,
			observeParents: true,
			navigation: {
				prevEl: section.querySelector( '.videos__prev' ),
				nextEl: section.querySelector( '.videos__next' ),
			},
			pagination: {
				el: section.querySelector( '.videos__pagination' ),
				clickable: true,
			},
			breakpoints: {
				0: { spaceBetween: 16 },
				768: { spaceBetween: 32 },
			},
			on: {
				slideChange: function() {
					Array.prototype.forEach.call( players, reset );
				},
			},
		} );

		window.addEventListener( 'load', function() {
			swiper.update();
		} );
	} );
}() );

/**
 * Case studies carousel.
 *
 * Wide cards, one and a bit on screen at a time, with the same controls the
 * other carousels use.
 */
( function() {
	'use strict';

	var sections = document.querySelectorAll( '.cases' );

	if ( ! sections.length ) {
		return;
	}

	Array.prototype.forEach.call( sections, function( section ) {
		var container = section.querySelector( '.cases__swiper' );

		if ( ! container ) {
			return;
		}

		var swiper = new Swiper( container, {
			modules: [ Navigation, Pagination ],
			slidesPerView: 'auto',
			spaceBetween: 40,
			speed: 500,
			watchOverflow: true,
			// Slide widths come from the stylesheet, so re-measure whenever the
			// layout around the carousel settles.
			observer: true,
			observeParents: true,
			navigation: {
				prevEl: section.querySelector( '.cases__prev' ),
				nextEl: section.querySelector( '.cases__next' ),
			},
			pagination: {
				el: section.querySelector( '.cases__pagination' ),
				clickable: true,
			},
			breakpoints: {
				0: { spaceBetween: 16 },
				768: { spaceBetween: 40 },
			},
		} );

		window.addEventListener( 'load', function() {
			swiper.update();
		} );
	} );
}() );
